import { showSpinner, hideSpinner } from './spinner';
import { env, assertEnv } from './env';

function loadEvoHostScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load Evo host script: ${src}`));
    document.head.appendChild(script);
  });
}

function mountMfe(): void {
  const container = document.getElementById('root');
  if (!container) throw new Error('Missing #root container');
  window.AccessEvo.mfes.add({
    name: env.mfeName,
    instanceId: crypto.randomUUID(),
    container,
    shell: 'border-with-header-no-remove',
    parameters: { instanceId: 'standalone-wrapper' },
  });
}

// Embedded inside Evo Tab / Navigator. Parent already has an authenticated
// session with Workspace-capable scopes; we just establish a postMessage
// bridge via EvoIframeApi and let the parent provide auth, button token and
// MFE registry. Our own OIDC / org fetch / Access Button launcher are all
// redundant here.
async function bootstrapEmbedded(): Promise<void> {
  showSpinner('Connecting to Evo…');
  const { EvoIframeApi } = await import('@access-evo/mfe-react-host');
  const api = new EvoIframeApi();

  // The MFE bundle (loaded later by AccessEvo.mfes.add) ships its own
  // @access-evo/react-app-api, whose getButtonToken probes window.accessButton
  // on the wrapper's own window. Evo's parent has the real launcher, but the
  // MFE can't reach across frames to find it. Install a thin stub that bridges
  // up to the parent's accessButton via EvoIframeApi. Re-login UX is not
  // replicated here; Evo's parent shell owns the session and surfaces auth
  // issues itself.
  window.accessButton = {
    getButtonToken: () => api.getButtonToken(),
  };

  await api.initialiseMfes();
  mountMfe();
  hideSpinner();
}

// Standalone (localhost dev or direct SWA URL hit). No Evo parent to lean on,
// so we run the full self-hosted boot: OIDC, org selection, header, button
// token exchange, then initialiseMfes with our own callbacks. All standalone-
// only modules are dynamic-imported so the embedded build stays free of
// oidc-client-ts and the standalone subgraph.
async function bootstrapStandalone(): Promise<void> {
  showSpinner('Signing you in…');
  const [
    { userManager, ensureAuthenticated, getAccessToken, getCurrentUser },
    { fetchOrgList, resolveOrgId },
    { fetchButtonTokenResponse },
    { renderHeader },
    { decodeJwt },
  ] = await Promise.all([
    import('./auth'),
    import('./org'),
    import('./buttonToken'),
    import('./header'),
    import('./jwt'),
  ]);

  await ensureAuthenticated();

  showSpinner('Loading your organisations…');
  const orgs = await fetchOrgList();
  const orgId = resolveOrgId(orgs);

  renderHeader({
    orgs,
    selectedOrgId: orgId,
    onSignOut: () => userManager.signoutRedirect(),
  });

  showSpinner('Preparing workspace…');

  // Dynamic import: only load the Access Button launcher in standalone mode.
  const { initialiseAccessButton } = await import('@workspace/access-button-launcher');
  initialiseAccessButton({
    environment: env.evoEnvironment,
    getToken: () => fetchButtonTokenResponse(orgId),
    positions: {
      parentElement: 'body',
      desktop: { top: 12, left: 12 },
    },
    closedZindex: 5,
  });

  await loadEvoHostScript(env.evoHostScript);

  await window.AccessEvo.initialiseMfes({
    hostName: 'kl-demo-host',
    environment: env.evoEnvironment,
    geography: 'uk',
    getAccessToken,
    getEvoUser: async () => {
      const user = await getCurrentUser();
      const claims = decodeJwt<Record<string, unknown>>(user.access_token);
      return {
        id: (claims.acid as string) ?? user.profile.sub,
        emailAddress: (claims.email as string) ?? user.profile.email ?? '',
        organisation: { id: orgId },
      };
    },
  });

  mountMfe();
  hideSpinner();
}

async function bootstrap(): Promise<void> {
  assertEnv();

  if (location.pathname === '/sign-in/callback') {
    const { userManager } = await import('./auth');
    await userManager.signinRedirectCallback();
    location.replace('/');
    return;
  }
  if (location.pathname === '/sign-out/callback') {
    const { userManager } = await import('./auth');
    await userManager.signoutRedirectCallback();
    await userManager.removeUser();
    location.replace('/');
    return;
  }

  const embedded = window.self !== window.top;
  if (embedded) document.documentElement.classList.add('embedded');

  if (embedded) {
    await bootstrapEmbedded();
  } else {
    await bootstrapStandalone();
  }
}

bootstrap().catch((err) => {
  console.error('Bootstrap failed:', err);
  showSpinner('Failed to start. Please reload.');
});
